import * as addressActions from '~/actions/address.actions';
import * as carrierActions from '~/actions/carrier-accounts.actions';
import { ActionKeys } from '~/actions/constants';
import * as gtmActions from '~/actions/gtm.actions';
import * as ordersActions from '~/actions/orders.actions';
import * as shipmentsActions from '~/actions/shipments.actions';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import * as userActions from '~/actions/user.actions';
import { UPS } from '~/constants/carriers';
import {
  STORE_CONNECTION_SUCCESS_COOKIE_NAME,
  USER_REGISTER_SUCCESS_COOKIE_NAME,
} from '~/constants/cookies';
import {
  BATCH_INITIATE_FLOW_EVENT,
  BATCH_PURCHASED_EVENT,
  BATCH_SHOW_RATES_EVENT,
  CARRIER_CONNECT_SUCCESS,
  FIRST_LABEL_PURCHASED,
  ORDERS_COUNT,
  SEVENDFLR,
  STORE_CONNECTION_SUCCESS_EVENT,
  UPS_MASTER_REGISTRATION_SUCCESS,
  USER_ADDED_ADDRESS,
  USER_ADDED_CREDITCARD,
  USER_REGISTER_EVENT,
  USER_SIGNED_IN,
} from '~/constants/gtm';
import { SHOW_PROGRESS_METER } from '~/constants/local-storage-keys';
import { CARRIER_REGISTRATION_SUCCESS } from '~/constants/user-events';
import * as Cookies from 'js-cookie';
import * as moment from 'moment';
import { call, put, select, takeLatest, take } from 'redux-saga/effects';
import {
  getAllOrdersCount,
  getIsLoading,
  getOrdersCount,
  getOrdersData,
  getSelectedOrders,
} from '~/selectors/orders.selectors';
import {
  getActivationData,
  getDefaultParcel,
  getUserAddress,
  getUserDataForFeatureTests,
  getUserId,
  getUserReturnAddress,
} from '~/selectors/user.selectors';
import { getCurrentUser } from '~/services/user.service';
import { Events, identify, track } from '~/utils/segment';
import { extractMarketingPayloadFromCookie } from '~/utils/utm-tracking';
import { selectState } from '..';
import {
  requestUpdatedTrack,
  trackAddressUpdate,
  trackOrderUpdate,
} from '~/utils/TrackingHelper/utils';
import { ADDRESS_TRACKING_EVENT, ORDER_TRACKING_EVENT } from '~/utils/TrackingHelper/types';
import { trackingHelper } from '~/utils/TrackingHelper';
import {
  getBatchEntryIds,
  getTrackedOrderDetailsByShipmentFromOrders,
} from '~/utils/TrackingHelper/trackedOrderDetails';
import { getRatesForBatchEntriesToBuy } from '~/selectors/batch-entry.composite.selectors';
import { getBatchEntryData } from '~/selectors/batch-entry.selectors';
import { getPaymentMethods } from '~/selectors/billing.selectors';
import { getLabelSettings } from '~/selectors/label-settings.selectors';
import { FeatureFlagUserData } from '~/utils/featureflagsV2/types';
import {
  getOnboarding2024FlagStorage,
  getOnboarding2024MonthlyVolume,
} from '~/pages/Onboarding2024/utils/onboarding';

export function registerSuccessCookie() {
  return Cookies.get(USER_REGISTER_SUCCESS_COOKIE_NAME) || 'false';
}

export function* sendRegisterSuccessEvent() {
  const registerSuccessCookieValue: string = yield call(registerSuccessCookie);

  if (registerSuccessCookieValue === 'true') {
    yield put(
      gtmActions.sendGTMEvent({
        eventName: USER_REGISTER_EVENT,
      }),
    );

    // Show the progress meter for new users
    localStorage.setItem(SHOW_PROGRESS_METER, 'true');

    const userResponse = yield call(getCurrentUser);
    const UTMCookies = Cookies.getJSON('shippoUTM');
    const marketingPayload = UTMCookies ? extractMarketingPayloadFromCookie(UTMCookies) : undefined;
    const storeReferral = localStorage.getItem('registration_store') ?? undefined;

    // Use the local storage to fetch the new onboarding flow related data
    const onboardingMonthlyVolume = yield call(getOnboarding2024MonthlyVolume);
    const isOnboardingFlagOn = yield call(getOnboarding2024FlagStorage);

    yield call(track, Events.registered, {
      first_name: userResponse?.data.first_name,
      last_name: userResponse?.data.last_name,
      email: userResponse?.data.email,
      userId: userResponse?.data.id.toString(),
      userId_integer: userResponse?.data.id,
      registration_date: userResponse?.data.date_joined,
      registration_source: marketingPayload?.source,
      registration_method: marketingPayload?.regMethod,
      registration_ad_param1: marketingPayload?.shAd1,
      registration_ad_param2: marketingPayload?.shAd2,
      registration_ad_param3: marketingPayload?.shAd3,
      registration_ad_param4: marketingPayload?.shAd4,
      registration_campaign: marketingPayload?.campaign,
      registration_click_identifier: marketingPayload?.attributionSnippet,
      registration_medium: marketingPayload?.medium,
      store_referral: storeReferral,
      monthly_shipment_volume: onboardingMonthlyVolume,
      is2024_signup_flow: isOnboardingFlagOn === 'true',
    });

    yield call(Cookies.remove, USER_REGISTER_SUCCESS_COOKIE_NAME, {
      domain: CONFIG.shippoRootUrl,
    });
  }
}

export function* getStoreConnectionSuccessCookie() {
  yield Cookies.get(STORE_CONNECTION_SUCCESS_COOKIE_NAME) || '';
}

export function* sendStoreConnectionSuccessEvent() {
  const storeConnectionSuccessCookieValue: string = yield call(getStoreConnectionSuccessCookie);
  if (storeConnectionSuccessCookieValue) {
    const payload: gtmActions.GTMEventPayload = {
      eventName: STORE_CONNECTION_SUCCESS_EVENT,
      data: {
        storeName: storeConnectionSuccessCookieValue,
      },
    };

    yield put(gtmActions.sendGTMEvent(payload));
    // @NOTE: Track events will be added back at a later date
    yield call(track, Events.store_connected, {
      store_name: storeConnectionSuccessCookieValue,
    });
    yield call(Cookies.remove, STORE_CONNECTION_SUCCESS_COOKIE_NAME, {
      domain: CONFIG.shippoRootUrl,
    });
  }
}

export function* sendOrderCount() {
  const ordersCount = yield select(getOrdersCount);
  const userOrderCount = yield select(getAllOrdersCount);
  const activationData = yield select(getActivationData);
  const isLoading = yield select(getIsLoading);
  const userId = yield select(getUserId);

  if (!isLoading && ordersCount !== activationData.ordersCount) {
    yield put(
      gtmActions.sendGTMEvent({
        eventName: ORDERS_COUNT,
        data: {
          ordersCount: ordersCount,
        },
      }),
    );
    yield put(userActions.updateActivationData({ ordersCount }));
    yield call(identify, userId, { orders_count: userOrderCount });
  }
}

export function* sendInitiateBatchCreateFlowEvent(
  action: ReturnType<typeof ordersActions.createBatchSuccess>,
) {
  const payload: gtmActions.GTMEventPayload = {
    eventName: BATCH_INITIATE_FLOW_EVENT,
    data: {
      batchId: action.payload.batch.id,
    },
  };

  yield put(gtmActions.sendGTMEvent(payload));
}

export function* sendShowBatchRatesEvent() {
  const payload: gtmActions.GTMEventPayload = {
    eventName: BATCH_SHOW_RATES_EVENT,
  };
  yield put(gtmActions.sendGTMEvent(payload));
  // @NOTE: Track events will be added back at a later date
  // yield call(track, Events.viewed_rates);
}

export function* sendBatchPurchasedEvent() {
  const getUserData: FeatureFlagUserData | null = yield selectState<FeatureFlagUserData | null>(
    getUserDataForFeatureTests,
  );
  const payload: gtmActions.GTMEventPayload = {
    eventName: BATCH_PURCHASED_EVENT,
  };

  // first label purchased GTM and Heap
  if (getUserData && !getUserData.hasPurchasedLabel) {
    yield put(userActions.updateFirstLabelPurchased());

    yield put(
      gtmActions.sendGTMEvent({
        eventName: FIRST_LABEL_PURCHASED,
      }),
    );
    // @NOTE: Track events will be added back at a later date
    // yield call(track, Events.first_label_purchase);
    yield put(sendTrackEvent('First Label Purchased', { timestamp: getUserData.dateJoined }));
    yield call([localStorage, localStorage.setItem], 'has_purchased_first_label', 'true');

    // 7-day first label purchase
    if (moment().isBefore(moment(getUserData.dateJoined).add(7, 'd'))) {
      yield put(
        gtmActions.sendGTMEvent({
          eventName: SEVENDFLR,
        }),
      );
    }
  }
  yield put(gtmActions.sendGTMEvent(payload));
  // ToDo: Verify with CJ that we really don't need it here.
  // This is causing two label_purchase event when done from the
  // side panel and adding payload to this one is difficult
  // yield call(track, Events.label_purchase);
  yield put(sendTrackEvent('Purchase Success'));
}

/**
 * User has saved an address, send event to GTM
 * @param action
 */
export function* sendAddressEvent(
  action: ReturnType<typeof addressActions.createAPIAddressSuccess>,
) {
  yield put(
    gtmActions.sendGTMEvent({
      eventName: USER_ADDED_ADDRESS,
      data: {
        isDefaultSender: action.payload.address.isDefaultSender,
        isDefaultReturn: action.payload.address.isDefaultReturn,
      },
    }),
  );

  yield call(track, Events.address_created, {
    is_default_sender: action.payload.address.isDefaultSender,
    is_default_return: action.payload.address.isDefaultReturn,
  });

  trackAddressUpdate(action.payload.trackingReference, ADDRESS_TRACKING_EVENT.operations.CREATED, {
    addressId: action.payload.address.id,
  });
}

/**
 * User added or updated credit card, send event to GTM
 */
export function* sendCreditCardEvent() {
  yield put(
    gtmActions.sendGTMEvent({
      eventName: USER_ADDED_CREDITCARD,
    }),
  );
  yield call(track, Events.credit_card_added);
}

/**
 * User registered a carrier account, send event to GTM
 * @param action
 */
export function* sendCreateCarrierAccountEvent(
  action: ReturnType<typeof carrierActions.createCarrierAccountSuccess>,
) {
  const carrierAccount = action.payload.carrierAccount;
  yield put(
    gtmActions.sendGTMEvent({
      eventName: CARRIER_REGISTRATION_SUCCESS,
      data: {
        carrierName: carrierAccount.carrier.name,
      },
    }),
  );
  // @NOTE: Track events will be added back at a later date
  // yield call(track, Events.registered_carrier, {
  //   carrierName: carrierAccount.carrier.name,
  // });
  // Send specific for UPS master
  if (carrierAccount.carrier && carrierAccount.carrier === UPS && carrierAccount.isShippoAccount) {
    yield put(
      gtmActions.sendGTMEvent({
        eventName: UPS_MASTER_REGISTRATION_SUCCESS,
      }),
    );
  }
}

/**
 * User connected a carrier account, send event to GTM
 * @param action
 */
export function* sendConnectCarrierAccountEvent(
  action: ReturnType<typeof carrierActions.connectCarrierAccountSuccess>,
) {
  yield put(
    gtmActions.sendGTMEvent({
      eventName: CARRIER_CONNECT_SUCCESS,
      data: {
        carrierName: action.payload.carrierAccount.carrier.name,
      },
    }),
  );

  yield call(track, Events.carrier_connected, {
    carrier_name: action.payload.carrierAccount.carrier.name,
  });
}

export function* trackOrderSelection(
  action:
    | ReturnType<typeof ordersActions.selectOrder>
    | ReturnType<typeof ordersActions.selectAllOrders>
    | ReturnType<typeof ordersActions.clearSelectedOrders>,
) {
  if (!action.trackEvent) {
    return;
  }
  const selectedOrders = yield select(getSelectedOrders);

  const trackingReference = trackingHelper.amendTrackingReferenceOrderSelection(
    trackingHelper.getTrackingReference(ORDER_TRACKING_EVENT.name, 'order_table', {}),
    selectedOrders,
  );

  if (trackingReference.orderQuantity === undefined) {
    if (
      action.type === ActionKeys.ORDERS_SELECT_ORDER ||
      action.type === ActionKeys.ORDERS_SELECT_ALL_ORDERS
    ) {
      // in classic orders page individual checkbox deselection, we can figure out the previously selected order
      // from the action payload...
      yield call(
        trackOrderUpdate,
        trackingHelper.amendTrackingReferenceOrders(trackingReference, Object.keys(action.payload)),
        ORDER_TRACKING_EVENT.operations.DESELECTED,
        {},
      );
    }

    // ...but deselecting all does not include the payload of previously selected orders
    yield call(trackOrderUpdate, trackingReference, ORDER_TRACKING_EVENT.operations.DESELECTED, {});
  } else {
    const batchEntryIds = getBatchEntryIds(selectedOrders);
    const ordersData = yield select(getOrdersData);
    const batchEntries = yield select(getBatchEntryData);
    const ratesForOrders = yield select(getRatesForBatchEntriesToBuy, { batchEntryIds });
    const defaultSenderAddress = yield select(getUserAddress);
    const defaultReturnAddress = yield select(getUserReturnAddress);
    const defaultParcelTemplate = yield select(getDefaultParcel);
    const paymentMethods = yield select(getPaymentMethods);
    const labelSettings = yield select(getLabelSettings);
    const { paymentType, stripeToken } = paymentMethods || {};

    const trackedOrderDetailsByShipment = getTrackedOrderDetailsByShipmentFromOrders({
      selectedOrders,
      ordersData,
      batchEntries,
      ratesForOrders,
      defaultSenderAddress,
      defaultReturnAddress,
      defaultParcelTemplate,
      paymentType,
      stripeToken,
      insuranceSettings: trackingReference.userDetails?.defaultInsuranceSetings,
      labelSettings,
    });

    const trackingReferenceWithOrderDetails =
      trackingHelper.amendTrackingReferenceTrackedOrderDetailsByShipment(
        trackingReference,
        trackedOrderDetailsByShipment,
      );

    yield call(
      trackOrderUpdate,
      trackingReferenceWithOrderDetails,
      ORDER_TRACKING_EVENT.operations.VIEWED,
      {},
    );
  }
}

export function* trackRateGenerated(
  action: ReturnType<typeof shipmentsActions.fetchShipmentRatesSuccess>,
) {
  const { payload, trackingReference } = action;

  if (payload.shipment?.status === 'SUCCESS') {
    yield call(requestUpdatedTrack, trackingReference, {
      url: `/shipments/${payload.shipment.objectId}`,
      isError: false,
      status: 200,
      message: undefined,
    });
  } else {
    yield call(requestUpdatedTrack, trackingReference, {
      url: `/shipments/${payload.shipment.objectId}`,
      isError: true,
      status: 200, // /shipments API always returns 200 but the shipment payload object can be have status of ERROR
      message: undefined,
    });
  }
}

export function* trackUserSignIn() {
  // before sending the gtm event, we need to make sure that
  // a) gtm has been initialized (done after user has been authenticated)
  // b) user data is set to store
  yield take(ActionKeys.USER_DID_AUTHENTICATE);
  yield take(ActionKeys.USER_FETCH_SUCCESS);

  yield put(gtmActions.sendGTMEvent({ eventName: USER_SIGNED_IN }));
}

export default [
  takeLatest(ActionKeys.USER_DID_AUTHENTICATE, sendRegisterSuccessEvent),
  takeLatest(ActionKeys.USER_DID_AUTHENTICATE, sendStoreConnectionSuccessEvent),
  takeLatest(ActionKeys.BATCH_CREATE_SUCCESS, sendInitiateBatchCreateFlowEvent),
  takeLatest(ActionKeys.ORDERS_BATCH_SAVE_PACKAGE_INFORMATION_SUCCESS, sendShowBatchRatesEvent),
  takeLatest(ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_SUCCESS, sendBatchPurchasedEvent),
  takeLatest(ActionKeys.APIADDRESS_CREATE_SUCCESS, sendAddressEvent),
  takeLatest(ActionKeys.USER_UPDATE_CREDIT_CARD_SUCCESS, sendCreditCardEvent),
  takeLatest(ActionKeys.CREATE_CARRIER_ACCOUNT_SUCCESS, sendCreateCarrierAccountEvent),
  takeLatest(ActionKeys.CONNECT_CARRIER_ACCOUNT_SUCCESS, sendConnectCarrierAccountEvent),
  takeLatest([ActionKeys.ORDERS_FETCH_SUCCESS], sendOrderCount),
  takeLatest(
    [
      ActionKeys.ORDERS_SELECT_ORDER,
      ActionKeys.ORDERS_SELECT_ALL_ORDERS,
      ActionKeys.ORDERS_CLEAR_SELECTED,
    ],
    trackOrderSelection,
  ),
  takeLatest(ActionKeys.SHIPMENT_FETCH_SUCCESS, trackRateGenerated),
  takeLatest(ActionKeys.LOGIN_SUCCESS, trackUserSignIn),
];
